// Opaque to the backend (stored as MediaItem.designData jsonb). Source of truth
// for both the interactive editor and the server print render.
export interface CardTransform {
  x: number;        // mm from top-left of the canvas (pre-bleed origin)
  y: number;        // mm
  width: number;    // mm (rendered box width)
  height: number;   // mm
  rotation: number; // degrees
}

// Linear gradient fill (additive). When present on a fill-bearing object it
// OVERRIDES the solid color. Both the interactive editor (Konva) and the print
// render (render-core) map `angle` → start/end points spanning the fill box.
export interface CardGradientStop {
  color: string;  // hex, rgba(...), or 8-digit hex (#rrggbbaa) for transparent fades
  offset: number; // 0..1 position along the gradient
}
export interface CardGradient {
  type: 'linear' | 'radial';
  angle: number;             // degrees, 0 = left→right, 90 = top→bottom (ignored for radial)
  stops: CardGradientStop[]; // >= 2
}

// addColorStop throws a SyntaxError on anything the canvas can't parse as a
// colour, and the throw kills the WHOLE draw — one bad stop blanks the editor
// canvas or the print render. Designs have carried the SVG idiom `none` (and an
// empty string from a cleared colour input), so normalise those to the CSS
// equivalent rather than letting a single layer take down the page.
export function gradientStopColor(color: string | null | undefined): string {
  const c = (color ?? '').trim();
  if (!c || c.toLowerCase() === 'none') return 'transparent';
  return c;
}

// Element controls shared by every layer type (all optional, additive).
export interface CardLayerCommon {
  opacity?: number;  // 0..1, default 1
  locked?: boolean;  // default false — not selectable/draggable/transformable
  hidden?: boolean;  // default false — not rendered
  /**
   * Directional fade-to-transparent for the element. When set, the element is
   * erased (destination-out) toward the chosen direction so it melts into the
   * background. Works for image, shape, svg and text layers; both the editor
   * (Konva) and the print render (render-core) apply the same geometry.
   *   radial   — vignette: opaque centre → clear at the box edges
   *   vertical — symmetric ↕: solid centre band, fades to clear at BOTH the top
   *              and bottom edges
   *   horizontal — symmetric ↔: solid centre band, fades to clear at BOTH the
   *              left and right edges
   *   top      — fades out toward the top edge
   *   bottom   — fades out toward the bottom edge
   *   left     — fades out toward the left edge
   *   right    — fades out toward the right edge
   *   diagonal — fades out toward the top-right corner (↗)
   *   diagonal2 — fades out toward the top-left corner (↖) — the mirror diagonal
   *   verticalHorizontal — combines vertical + horizontal: a RECTANGULAR fade on
   *                all four edges (corners clear most), unlike the elliptical radial
   */
  fade?: 'radial' | 'vertical' | 'horizontal' | 'verticalHorizontal' | 'top' | 'bottom' | 'left' | 'right' | 'diagonal' | 'diagonal2';
  /**
   * Fade STRENGTH — how far the fade reaches into the element, 0..1 (default 0.5).
   * Clamped to [0.05, 1] at render time. Higher = the element fades away more:
   *   directional — gradient runs from OPAQUE at the chosen edge to TRANSPARENT
   *                 at fraction `fadeStrength` across the box (1 = across the
   *                 whole element; 0.2 = only a narrow edge band).
   *   diagonal    — same, measured along the top-right→bottom-left diagonal.
   *   radial      — vignette REACH: how far the fade extends inward from the box
   *                 edge. The fade always reaches FULL transparency exactly at
   *                 the edge on all four sides (the gradient is elliptical, so it
   *                 touches every edge — not just the corners). 1 = fades from the
   *                 very centre; small = only a thin edge band fades.
   */
  fadeStrength?: number;
  /**
   * Radial fade SOFTNESS — how soft/blurred the radial transition is, 0..1
   * (default 0.5). Only used for `fade === 'radial'`. 1 = the blend is spread
   * gradually across the whole fade region (very soft, feathered edge); 0 = the
   * element stays solid then drops off sharply right at the edge (a hard ring).
   * Independent of `fadeStrength`, which sets how deep the fade reaches.
   */
  fadeSoftness?: number;
}

export interface CardImageLayer extends CardLayerCommon {
  type: 'image';
  sourceKey: string;        // S3 key of the uploaded source photo. May be '' when the frame is empty (no photo yet).
  url?: string;             // transient signed/object URL for rendering (not persisted as source of truth)
  transform: CardTransform;
  z: number;
  // crop within the source image, 0..1 fractions (optional)
  crop?: { x: number; y: number; width: number; height: number };
  // Optional frame clip shape. When set, the image is clipped to this path
  // (the viewBox is scaled to fill the transform box) and cover-fit inside the box.
  // An empty frame (frame set, sourceKey '') renders as a photo placeholder.
  frame?: { paths: { d: string }[]; viewBox: { width: number; height: number } };
  /**
   * How the photo sits WITHIN its `frame` (zoom / pan / rotate). Only meaningful
   * when `frame` is set. Undefined = the legacy default: centred cover-fit.
   *   scale    — zoom multiplier OVER the cover-fit scale (>= 1; 1 = exactly
   *              cover-fit, 2 = twice as zoomed in). Values < 1 are clamped to 1
   *              at render time so the frame is always fully covered.
   *   offsetX  — horizontal pan as a fraction of the box WIDTH  (0 = centred).
   *   offsetY  — vertical   pan as a fraction of the box HEIGHT (0 = centred).
   *              Pan is clamped at render time so the photo never reveals a gap.
   *   rotation — rotation of the photo inside the frame, in degrees (0 = none).
   * Both the editor (Konva) and the print render (render-core) feed these through
   * the shared `framedImageLayout` helper so they place the photo identically.
   */
  frameFit?: { scale: number; offsetX: number; offsetY: number; rotation: number };
  // Mirror the photo horizontally / vertically. Applied about the image centre in
  // both the editor and the print render. Default false.
  flipH?: boolean;
  flipV?: boolean;
}

export interface CardTextLayer extends CardLayerCommon {
  type: 'text';
  content: string;
  font: string;             // font-family
  /** Bold weight (700) instead of regular (400). Default false. Real font-weight —
   *  NOT encoded into `font` (legacy " Bold"-suffixed families are normalized away
   *  on ingest; see normalizeTextLayerFont in templates/types.ts). */
  bold?: boolean;
  size: number;             // pt
  color: string;            // hex
  /** Optional gradient fill; OVERRIDES `color` when set. */
  gradient?: CardGradient;
  align?: 'left' | 'center' | 'right';
  transform: CardTransform;
  z: number;
}

export interface CardShapeLayer extends CardLayerCommon {
  type: 'shape';
  shape: 'rect' | 'line';
  fill?: string;
  /** Optional gradient fill; OVERRIDES `fill` when set. */
  fillGradient?: CardGradient;
  stroke?: string;
  strokeWidth?: number; // mm
  radiusMm?: number;    // rect corner radius (mm)
  transform: CardTransform;
  z: number;
}

export interface CardSvgPath {
  d: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface CardSvgLayer extends CardLayerCommon {
  type: 'svg';
  paths: CardSvgPath[];
  viewBox: { width: number; height: number };
  /** Optional single-color override applied to all paths (for icon recolouring). */
  color?: string;
  /** Optional gradient fill applied to all paths; OVERRIDES `color` when set. */
  gradient?: CardGradient;
  /** Layer-level stroke colour applied to all paths; overrides per-path stroke when set. */
  stroke?: string;
  /** Layer-level stroke width in mm; overrides per-path strokeWidth when set. */
  strokeWidth?: number; // mm
  transform: CardTransform;
  z: number;
}

export type CardLayer = CardImageLayer | CardTextLayer | CardShapeLayer | CardSvgLayer;

export interface SlotDesign {
  slotKey: string;
  canvas: { widthMm: number; heightMm: number; bleedMm: number; safeMm?: number };
  // `image` is the object KEY of a paper background photo (see Paper.backgroundImage).
  // Resolved through LoadedImages at draw time. Precedence: image → gradient → color.
  background: { color: string; gradient?: CardGradient; image?: string };
  layers: CardLayer[];
}

// ─────────────────────────────────────────────────────────────────────────────
// framedImageLayout — single source of truth for HOW a photo sits inside its
// frame box, shared by the editor (Konva) and the print render (render-core) so
// they place the photo pixel-identically. Pure geometry, no canvas dependency.
//
// Given the source image natural size (nw×nh) and the frame box size (w×h, in the
// same px units), plus the optional `fit` (zoom/pan/rotate), it returns the photo
// drawn as a `dw×dh` rectangle, centred at (cx, cy) in box space (origin = box
// top-left), rotated `rotationDeg` about that centre.
//
// Coverage guarantee: the returned size/scale ALWAYS fully covers the box at the
// requested rotation (so a rotated or panned photo never reveals a gap), and the
// pan is clamped to keep it that way. `fit === undefined` (or scale 1 / no pan /
// no rotation) reproduces the legacy centred cover-fit exactly.
// ─────────────────────────────────────────────────────────────────────────────
export interface FramedImageLayout {
  dw: number;          // drawn photo width  (box px)
  dh: number;          // drawn photo height (box px)
  cx: number;          // photo centre X in box space (origin = box top-left)
  cy: number;          // photo centre Y in box space
  rotationDeg: number; // rotation about (cx, cy)
}
export function framedImageLayout(
  nw: number,
  nh: number,
  w: number,
  h: number,
  fit?: { scale?: number; offsetX?: number; offsetY?: number; rotation?: number },
): FramedImageLayout {
  const safeNw = nw > 0 ? nw : 1;
  const safeNh = nh > 0 ? nh : 1;
  const rotationDeg = fit?.rotation ?? 0;
  const rad = (rotationDeg * Math.PI) / 180;
  const ac = Math.abs(Math.cos(rad));
  const as = Math.abs(Math.sin(rad));

  // Min photo size needed to cover the box at this rotation. The box corner
  // farthest along the photo's local axes sits at half-extent
  // (w/2)·|cos| + (h/2)·|sin| (and the transpose for the other axis), so the
  // photo must be at least twice that wide/tall. At rotation 0 this collapses to
  // the familiar max(w/nw, h/nh) cover-fit.
  const needW = w * ac + h * as;   // required dw
  const needH = w * as + h * ac;   // required dh
  const coverScale = Math.max(needW / safeNw, needH / safeNh);
  // zoom multiplies the cover scale. 1 = exactly cover-fit; > 1 zooms in; < 1
  // zooms OUT so the photo is smaller than the frame (letterboxed, may leave gaps
  // the paper shows through). Floored so it can never invert or vanish.
  const zoom = Math.max(0.05, fit?.scale ?? 1);
  const scale = coverScale * zoom;
  const dw = safeNw * scale;
  const dh = safeNh * scale;

  // Allowable pan, measured in the photo's own (rotated) frame: how far the box
  // centre can slide before a corner pokes outside the photo. Never negative.
  const slackX = Math.max(0, dw / 2 - needW / 2);
  const slackY = Math.max(0, dh / 2 - needH / 2);

  // Desired pan in box coords (offset is a fraction of the box w/h).
  const panBoxX = (fit?.offsetX ?? 0) * w;
  const panBoxY = (fit?.offsetY ?? 0) * h;
  // Rotate the desired pan into the photo frame, clamp per-axis, rotate back so
  // the photo always covers the box regardless of rotation.
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const pxImg = panBoxX * cos + panBoxY * sin;
  const pyImg = -panBoxX * sin + panBoxY * cos;
  const cxImg = Math.max(-slackX, Math.min(slackX, pxImg));
  const cyImg = Math.max(-slackY, Math.min(slackY, pyImg));
  const panX = cxImg * cos - cyImg * sin;
  const panY = cxImg * sin + cyImg * cos;

  return { dw, dh, cx: w / 2 + panX, cy: h / 2 + panY, rotationDeg };
}

// Transform (in the layer's pre-bleed mm coordinate space) that makes a layer
// cover the WHOLE sheet including bleed — used for a customer's "bring your own
// design" overlay so it fills the card edge-to-edge. The layer origin sits at
// -bleed so its top-left lands on the very corner of the bleed area, and its
// size spans trim + bleed on both sides. Matches render-core's placement math:
// pixel-left = bleedPx + x·ppm, so x = -bleedMm puts it at pixel 0.
export function fullBleedTransform(
  canvas: { widthMm: number; heightMm: number; bleedMm: number },
): { x: number; y: number; width: number; height: number; rotation: number } {
  // `|| 0` normalises -0 (when bleedMm is 0) to +0 so the stored transform is tidy.
  return {
    x: -canvas.bleedMm || 0,
    y: -canvas.bleedMm || 0,
    width: canvas.widthMm + canvas.bleedMm * 2,
    height: canvas.heightMm + canvas.bleedMm * 2,
    rotation: 0,
  };
}

export const MM_PER_INCH = 25.4;
// pixels per mm at a given DPI
export function pxPerMm(dpi: number): number {
  return dpi / MM_PER_INCH;
}
// full pixel dimensions of a slot's canvas INCLUDING bleed on all sides, at dpi
export function slotPixelSize(canvas: SlotDesign['canvas'], dpi: number): { width: number; height: number } {
  const ppm = pxPerMm(dpi);
  const totalW = canvas.widthMm + canvas.bleedMm * 2;
  const totalH = canvas.heightMm + canvas.bleedMm * 2;
  return { width: Math.round(totalW * ppm), height: Math.round(totalH * ppm) };
}

// ─────────────────────────────────────────────────────────────────────────────
// svgGlyphBox — tight bounding box of an SVG icon's actual drawn geometry, in
// viewBox coordinates. Shared by the editor (Konva) and the print render
// (render-core) so a fade centres on the VISIBLE glyph rather than the full
// (often heavily-padded) viewBox. Without this, an elliptical radial fade on an
// icon reaches full transparency at the empty box margins while the glyph sits
// in the kept-solid centre and barely fades — the user's "centre never
// disappears" bug.
//
// It walks every path's `d` string, tracking the current point so relative
// commands resolve correctly, and unions every anchor + control point. For
// Bézier/quadratic curves the convex hull of the control points contains the
// curve, so the resulting box is a safe (never-clipping) superset of the true
// outline — slightly loose for deep curves but always tighter than the viewBox
// and pixel-identical across editor and print. Arc (A/a) endpoints are included;
// their radii bulge is ignored (acceptable for a fade centre — icons rarely rely
// on it, and packed arc flags could otherwise mis-parse).
//
// Returns null when nothing parses (caller then falls back to the full viewBox).
export function svgGlyphBox(
  paths: { d: string }[],
  viewBox: { width: number; height: number },
): { left: number; top: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  // Matches signed ints/decimals with optional exponent: -1.5, .5, 1., 1e-3
  const numRe = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g;
  // Splits a `d` string into [command letter, its number list] tuples.
  const cmdRe = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;

  const add = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  for (const path of paths) {
    if (!path.d) continue;
    let cx = 0;    // current point
    let cy = 0;
    let sx = 0;    // subpath start (for Z)
    let sy = 0;
    cmdRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = cmdRe.exec(path.d)) !== null) {
      const cmd = m[1];
      const nums = (m[2].match(numRe) ?? []).map(Number);
      const rel = cmd === cmd.toLowerCase();
      const abs = (dx: number, dy: number) => rel ? [cx + dx, cy + dy] as const : [dx, dy] as const;
      const up = cmd.toUpperCase();
      let i = 0;
      if (up === 'M' || up === 'L' || up === 'T') {
        // First M pair moves; subsequent pairs are implicit L (T is a smooth
        // quadratic — its endpoint is what we track). All are (x,y) pairs.
        for (; i + 1 < nums.length; i += 2) {
          const [x, y] = abs(nums[i], nums[i + 1]);
          add(x, y);
          cx = x; cy = y;
          if (up === 'M' && i === 0) { sx = x; sy = y; }
        }
      } else if (up === 'H') {
        for (; i < nums.length; i++) {
          const x = rel ? cx + nums[i] : nums[i];
          add(x, cy);
          cx = x;
        }
      } else if (up === 'V') {
        for (; i < nums.length; i++) {
          const y = rel ? cy + nums[i] : nums[i];
          add(cx, y);
          cy = y;
        }
      } else if (up === 'C') {
        for (; i + 5 < nums.length; i += 6) {
          const [x1, y1] = abs(nums[i], nums[i + 1]);
          const [x2, y2] = abs(nums[i + 2], nums[i + 3]);
          const [x, y] = abs(nums[i + 4], nums[i + 5]);
          add(x1, y1); add(x2, y2); add(x, y);
          cx = x; cy = y;
        }
      } else if (up === 'S' || up === 'Q') {
        // S: (x2,y2,x,y); Q: (x1,y1,x,y). Both 4-number groups where the last
        // pair is the endpoint and the first pair is a control point.
        for (; i + 3 < nums.length; i += 4) {
          const [cxp, cyp] = abs(nums[i], nums[i + 1]);
          const [x, y] = abs(nums[i + 2], nums[i + 3]);
          add(cxp, cyp); add(x, y);
          cx = x; cy = y;
        }
      } else if (up === 'A') {
        // rx,ry,rot,large,sweep,x,y — only the endpoint is a coordinate.
        for (; i + 6 < nums.length; i += 7) {
          const [x, y] = abs(nums[i + 5], nums[i + 6]);
          add(x, y);
          cx = x; cy = y;
        }
      } else if (up === 'Z') {
        cx = sx; cy = sy;
      }
    }
  }

  if (!Number.isFinite(minX) || maxX <= minX || maxY <= minY) return null;
  // Clamp to the viewBox so a stray control point can't push the fade box out of
  // the drawn area (keeps editor/print sane).
  const vw = viewBox.width > 0 ? viewBox.width : maxX;
  const vh = viewBox.height > 0 ? viewBox.height : maxY;
  const l = Math.max(0, minX);
  const tp = Math.max(0, minY);
  const r = Math.min(vw, maxX);
  const b = Math.min(vh, maxY);
  if (r <= l || b <= tp) return null;
  return { left: l, top: tp, width: r - l, height: b - tp };
}
